import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { Flex } from "@radix-ui/themes";
import { Accordion as RadixAccordion } from "radix-ui";
import type React from "react";

import { Icon } from "@components/Icon";
import { theme } from "@themes/themes";

interface AccordionProps {
  label: React.ReactNode;
  value?: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  disabled?: boolean;
  triggerTestId?: string;
  contentTestId?: string;
}

export const Accordion: React.FC<AccordionProps> = ({
  label,
  value,
  children,
  defaultOpen = false,
  open,
  onOpenChange,
  className,
  disabled = false,
  triggerTestId,
  contentTestId,
}) => (
  <StyledRoot
    className={className}
    defaultValue={defaultOpen ? "item" : undefined}
    disabled={disabled}
    type="single"
    value={open !== undefined ? (open ? "item" : "") : undefined}
    collapsible
    onValueChange={onOpenChange ? (v) => onOpenChange(v === "item") : undefined}
  >
    <StyledItem value="item">
      <StyledHeader asChild>
        <StyledTrigger data-testid={triggerTestId}>
          <Label>{label}</Label>
          <Right>
            {value && <Value>{value}</Value>}
            <ArrowIcon>
              <Icon icon="ri-arrow-down-s-line" size={16} />
            </ArrowIcon>
          </Right>
        </StyledTrigger>
      </StyledHeader>
      <StyledContent data-testid={contentTestId}>
        <ContentInner>{children}</ContentInner>
      </StyledContent>
    </StyledItem>
  </StyledRoot>
);

const StyledRoot = styled(RadixAccordion.Root)`
  width: 100%;
`;

const StyledItem = styled(RadixAccordion.Item)`
  border: 1px solid ${theme.color("gray-5")};
  background-color: ${theme.color("gray-4")};
  border-radius: 8px;
`;

const StyledHeader = styled(RadixAccordion.Header)`
  margin: 0;
`;

const StyledTrigger = styled(RadixAccordion.Trigger)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 12px;
  background: none;
  border: none;
  cursor: pointer;
  outline: none;

  &[data-disabled] {
    cursor: not-allowed !important;
    opacity: 0.5;
  }
`;

const Label = styled.span`
  color: ${theme.color("gray-11")};
  ${theme.font.body12};
`;

const Right = styled.span`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Value = styled.span`
  color: ${theme.color("accent-contrast")};
  ${theme.font.body12};
`;

const ArrowIcon = styled(Flex)`
  color: ${theme.color("accent-contrast")};
  align-items: center;
  transition: transform 0.2s ease;

  width: 16px;
  height: 16px;

  [data-state="open"] & {
    transform: rotate(180deg);
  }
`;

const slideDown = keyframes`
  from { height: 0; opacity: 0; }
  to { height: var(--radix-accordion-content-height); opacity: 1; }
`;

const slideUp = keyframes`
  from { height: var(--radix-accordion-content-height); opacity: 1; }
  to { height: 0; opacity: 0; }
`;

const StyledContent = styled(RadixAccordion.Content)`
  animation-duration: 0.2s;
  animation-timing-function: ease;
  &[data-state="open"] {
    animation-name: ${slideDown};
    overflow: visible;
  }
  &[data-state="closed"] {
    animation-name: ${slideUp};
    overflow: hidden;
  }
`;

const ContentInner = styled.div`
  padding: 0 12px 12px 12px;
`;
